/**
 * Thin REST client for the Sessions backend.
 *
 * Two Cognito-authorized endpoints are wired (see
 * `infrastructure/lib/stacks/sessions-stack.ts`):
 * - `POST /sessions/state` — idempotent lifecycle events (`uuid#type#clientEventId`).
 * - `GET /sessions/mine` — the caller's sessions, ordered by start.
 *
 * The base URL is read from `VITE_SESSIONS_API_URL` (the `SessionsApiUrl` CDK
 * output). It is read directly here (rather than added to the shared `env`
 * module) to keep this slice purely additive and avoid touching shared config.
 */

import { fetchAuthSession } from 'aws-amplify/auth'
import type {
  AhpSyncStatus,
  CancellationReason,
  DeparturePreview,
  Session,
  SessionCancellationRequest,
  SessionChecklist,
  SessionJourney,
  SessionLifecycleEvent,
  SessionLifecycleState,
} from '@shared/sessions/types'
import type { JourneyBackfillFix } from '@shared/sessions/journey-backfill'

/**
 * Build the Error for a non-2xx response. Session endpoints return structured
 * bodies like `{"error":"PREMATURE_CHECK_IN","message":"You appear to…"}` —
 * when a human-readable `message` is present, surface it on its own
 * (2026-10-07 field bug: the provider was shown the raw
 * `POST /sessions/state failed (409): {…json…}` line). Anything else falls
 * back to the labelled status + raw body so diagnostics stay intact.
 */
async function responseError(
  label: string,
  res: Response,
): Promise<Error & { status: number; code?: string }> {
  const detail = await res.text().catch(() => '')
  let message = `${label} failed (${res.status}): ${detail}`
  let code: string | undefined
  try {
    const parsed = JSON.parse(detail) as { message?: unknown; error?: unknown }
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      message = parsed.message.trim()
    }
    // The machine-readable code (e.g. `JOURNEY_NOT_ACTIVE`) — callers branch on it.
    if (typeof parsed.error === 'string' && parsed.error.trim()) code = parsed.error.trim()
  } catch {
    // Not JSON — fall through to the raw detail.
  }
  return Object.assign(new Error(message), { status: res.status, ...(code ? { code } : {}) })
}

/** Shape returned by `POST /sessions/state`. */
export interface SubmitStateResponse {
  uuid: string
  /** `linked`: a session call was attached without recording a start/end. */
  status: 'applied' | 'duplicate' | 'linked'
  lifecycleState: SessionLifecycleState
  kmTracked: number | null
  ahpSync?: AhpSyncStatus
  /**
   * Session fields the server just wrote (or removed → null), so the client
   * can patch its row in place without a refetch — see
   * `LifecycleEventResult.patch`.
   */
  patch?: Partial<Session>
}

/** Shape returned by `GET /sessions/mine`. */
export type SessionsAudience = 'provider' | 'member' | 'linked_member_user' | 'linked_independent'

/** True when list items should show provider-first (member / linked lenses). */
export function isMemberSessionsAudience(audience: SessionsAudience): boolean {
  return audience !== 'provider'
}

/** An Independent's followed member (first name only — never the email). */
export interface FollowedMemberSummary {
  firstName: string
}

/** A member this Independent is marked "Can give consent" for (session recording). */
export interface ConsentGiverSummary {
  firstName: string
  authority: 'parent_guardian' | 'other'
}

export interface SessionFollowingSummary {
  followedMembers: FollowedMemberSummary[]
  consentGiverFor: ConsentGiverSummary[]
}

/**
 * True for an Independent following members' sessions (`linked_independent`):
 * read-only member lens, labelled by the member, no cancel or contact actions.
 */
export function isFollowerSessionsAudience(audience: SessionsAudience): boolean {
  return audience === 'linked_independent'
}

export interface GetMySessionsResponse {
  sessions: Session[]
  /** Which lens the backend used — provider schedule vs member schedule. */
  audience?: SessionsAudience
  /** Present for Independents: the members they follow with sessions read. */
  followedMembers?: FollowedMemberSummary[]
}

export interface MySessionsResult {
  sessions: Session[]
  audience: SessionsAudience
  followedMembers: FollowedMemberSummary[]
}

function normalizeFollowedMembers(value: unknown): FollowedMemberSummary[] {
  if (!Array.isArray(value)) return []
  return value
    .map((m) => (typeof m?.firstName === 'string' ? { firstName: m.firstName } : null))
    .filter((m): m is FollowedMemberSummary => m != null)
}

function normalizeConsentGiverFor(value: unknown): ConsentGiverSummary[] {
  if (!Array.isArray(value)) return []
  return value
    .map((m) =>
      typeof m?.firstName === 'string'
        ? { firstName: m.firstName, authority: m.authority === 'parent_guardian' ? 'parent_guardian' : 'other' }
        : null,
    )
    .filter((m): m is ConsentGiverSummary => m != null)
}

function getBaseUrl(): string {
  const url = import.meta.env.VITE_SESSIONS_API_URL ?? ''
  if (!url) {
    throw new Error(
      'VITE_SESSIONS_API_URL is not set. Deploy the SessionsStack and copy its ' +
        'SessionsApiUrl output into frontend/.env.local to use the API repository.',
    )
  }
  return url.replace(/\/$/, '')
}

/**
 * Resolve the Cognito **ID token**. API Gateway's Cognito User Pools authorizer
 * expects the ID token (not the access token), so we send `idToken` here.
 */
async function getIdToken(): Promise<string | null> {
  const session = await fetchAuthSession()
  return session.tokens?.idToken?.toString() ?? null
}

/**
 * GET the caller's sessions. Optional `from` (epoch ms) / `limit` query params
 * mirror the backend; omitting them lets the backend default the window so the
 * Today / Upcoming / Recent groups are populated. Throws on a non-2xx response.
 */
export async function getMySessions(opts?: {
  fromMs?: number
  limit?: number
}): Promise<MySessionsResult> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const query = new URLSearchParams()
  if (opts?.fromMs != null) query.set('from', String(opts.fromMs))
  if (opts?.limit != null) query.set('limit', String(opts.limit))
  const suffix = query.toString() ? `?${query.toString()}` : ''

  const res = await fetch(`${getBaseUrl()}/sessions/mine${suffix}`, {
    method: 'GET',
    headers: {
      Authorization: token,
    },
  })

  if (!res.ok) throw await responseError('GET /sessions/mine', res)

  const data = (await res.json()) as GetMySessionsResponse
  return {
    sessions: Array.isArray(data.sessions) ? data.sessions : [],
    audience: normalizeAudience(data.audience),
    followedMembers: normalizeFollowedMembers(data.followedMembers),
  }
}

/**
 * GET `/sessions/mine?summary=following` — the members the caller follows
 * (empty for anyone who is not an Independent follower). Drives whether an
 * Independent sees the Sessions nav. Throws on a non-2xx response.
 */
export async function getSessionFollowingSummary(): Promise<SessionFollowingSummary> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')
  const res = await fetch(`${getBaseUrl()}/sessions/mine?summary=following`, {
    method: 'GET',
    headers: { Authorization: token },
  })
  if (!res.ok) throw await responseError('GET /sessions/mine?summary=following', res)
  const data = (await res.json()) as { followedMembers?: unknown; consentGiverFor?: unknown }
  return {
    followedMembers: normalizeFollowedMembers(data.followedMembers),
    consentGiverFor: normalizeConsentGiverFor(data.consentGiverFor),
  }
}

function normalizeAudience(audience: SessionsAudience | undefined): SessionsAudience {
  return audience === 'member'
    ? 'member'
    : audience === 'linked_member_user'
      ? 'linked_member_user'
      : audience === 'linked_independent'
        ? 'linked_independent'
        : 'provider'
}

/**
 * GET one session with its full GPS evidence (`driveSegments`, departure
 * segments, all journey pings) — `GET /sessions/mine?uuid=`. List rows omit
 * those heavy fields. Resolves `null` when the session is not visible to the
 * caller (404).
 */
export async function getMySession(
  uuid: string,
): Promise<{ session: Session; audience: SessionsAudience } | null> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const query = new URLSearchParams({ uuid })
  const res = await fetch(`${getBaseUrl()}/sessions/mine?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: token,
    },
  })

  if (res.status === 404) return null
  if (!res.ok) throw await responseError('GET /sessions/mine?uuid', res)

  const data = (await res.json()) as { session?: Session; audience?: SessionsAudience }
  if (!data.session) return null
  return { session: data.session, audience: normalizeAudience(data.audience) }
}

/** POST an idempotent lifecycle event. Throws on a non-2xx response. */
export async function postSessionState(
  event: SessionLifecycleEvent,
): Promise<SubmitStateResponse> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(`${getBaseUrl()}/sessions/state`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(event),
  })

  if (!res.ok) throw await responseError('POST /sessions/state', res)

  return (await res.json()) as SubmitStateResponse
}

/** `POST /sessions/state` with `previewDeparture: true` — dry run, writes nothing. */
export async function postDeparturePreview(
  event: SessionLifecycleEvent,
  signal?: AbortSignal,
): Promise<DeparturePreview> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(`${getBaseUrl()}/sessions/state`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ ...event, previewDeparture: true }),
    signal,
  })

  if (!res.ok) throw await responseError('POST /sessions/state (preview)', res)

  return (await res.json()) as DeparturePreview
}

export interface JourneyStateResponse {
  uuid: string
  journey: SessionJourney
  leaveByAt: number | null
  preShiftChecklist: SessionChecklist | null
  mapsUrl: string | null
  /** Live lifecycle (e.g. `cancelled`) so polling views stay honest. */
  lifecycleState?: SessionLifecycleState | null
  /** Member's pending cancellation request, when one exists. */
  cancellationRequested?: SessionCancellationRequest | null
}

async function journeyFetch(path: string, init: RequestInit): Promise<Response> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')
  return fetch(`${getBaseUrl()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...(init.headers ?? {}),
    },
  })
}

/** GET journey + prep state for a session. */
export async function getSessionJourney(sessionUuid: string): Promise<JourneyStateResponse> {
  const res = await journeyFetch(`/sessions/${encodeURIComponent(sessionUuid)}/journey`, {
    method: 'GET',
  })
  if (!res.ok) throw await responseError(`GET /sessions/${sessionUuid}/journey`, res)
  return (await res.json()) as JourneyStateResponse
}

/** POST pre-shift checklist responses. */
export async function postPreShiftChecklist(
  sessionUuid: string,
  body: {
    responses: Record<string, boolean>
    completed?: boolean
    providerLocation?: { lat: number; lng: number }
  },
): Promise<{
  uuid: string
  preShiftChecklist: SessionChecklist
  leaveByAt?: number | null
  travelDurationMs?: number | null
}> {
  const res = await journeyFetch(
    `/sessions/${encodeURIComponent(sessionUuid)}/pre-shift/checklist`,
    { method: 'POST', body: JSON.stringify(body) },
  )
  if (!res.ok) throw await responseError('POST pre-shift checklist', res)
  return (await res.json()) as {
    uuid: string
    preShiftChecklist: SessionChecklist
    leaveByAt?: number | null
    travelDurationMs?: number | null
  }
}

/** POST journey start with first location ping. */
export async function postJourneyStart(
  sessionUuid: string,
  ping: {
    lat: number
    lng: number
    timestamp?: number
    accuracy?: number
    stage?: number
    etaMinutes?: number
    locationSource?: 'gps' | 'estimate'
  },
): Promise<{ uuid: string; journey: SessionJourney; leaveByAt: number | null }> {
  const res = await journeyFetch(`/sessions/${encodeURIComponent(sessionUuid)}/journey/start`, {
    method: 'POST',
    body: JSON.stringify(ping),
  })
  if (!res.ok) throw await responseError('POST journey/start', res)
  return (await res.json()) as { uuid: string; journey: SessionJourney; leaveByAt: number | null }
}

/** POST the explicit "I've arrived" action (only valid while en_route). */
export async function postJourneyArrive(
  sessionUuid: string,
  ping?: {
    lat: number
    lng: number
    timestamp?: number
    accuracy?: number
  },
): Promise<{ uuid: string; journey: SessionJourney; status: 'applied' | 'duplicate' }> {
  const res = await journeyFetch(`/sessions/${encodeURIComponent(sessionUuid)}/journey/arrive`, {
    method: 'POST',
    body: JSON.stringify(ping ?? {}),
  })
  if (!res.ok) throw await responseError('POST journey/arrive', res)
  return (await res.json()) as {
    uuid: string
    journey: SessionJourney
    status: 'applied' | 'duplicate'
  }
}

/** POST the member's "can't make it" cancellation request (idempotent). */
export async function postCancelRequest(
  sessionUuid: string,
  body: { reason: CancellationReason; note?: string },
): Promise<{
  uuid: string
  status: 'applied' | 'duplicate'
  cancellationRequested: SessionCancellationRequest
}> {
  const res = await journeyFetch(`/sessions/${encodeURIComponent(sessionUuid)}/cancel-request`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await responseError('POST cancel-request', res)
  return (await res.json()) as {
    uuid: string
    status: 'applied' | 'duplicate'
    cancellationRequested: SessionCancellationRequest
  }
}

/** POST an additional journey location ping (max 5 total). */
export async function postJourneyPing(
  sessionUuid: string,
  ping: {
    lat: number
    lng: number
    timestamp?: number
    accuracy?: number
    stage?: number
    etaMinutes?: number
    locationSource?: 'gps' | 'estimate'
    isMock?: boolean
  },
): Promise<{ uuid: string; journey: SessionJourney }> {
  const res = await journeyFetch(`/sessions/${encodeURIComponent(sessionUuid)}/journey/ping`, {
    method: 'POST',
    body: JSON.stringify(ping),
  })
  if (!res.ok) throw await responseError('POST journey/ping', res)
  return (await res.json()) as { uuid: string; journey: SessionJourney }
}

/**
 * POST a BACKFILL batch of replayed background fixes (own timestamps) to the
 * journey track — see shared/sessions/journey-backfill.ts. Same endpoint as
 * the live ping; the `backfill` array selects the batch path.
 */
export async function postJourneyBackfill(
  sessionUuid: string,
  backfill: JourneyBackfillFix[],
): Promise<{ uuid: string; journey: SessionJourney; accepted: number; rejected: number }> {
  const res = await journeyFetch(`/sessions/${encodeURIComponent(sessionUuid)}/journey/ping`, {
    method: 'POST',
    body: JSON.stringify({ backfill }),
  })
  if (!res.ok) throw await responseError('POST journey/ping (backfill)', res)
  return (await res.json()) as {
    uuid: string
    journey: SessionJourney
    accepted: number
    rejected: number
  }
}
